"use client";
import { lazy, Suspense, useRef, useState, useEffect } from "react";
import {
  universityLeadership,
  medhaviGroupLeadership,
  industryWISEAwardingBody,
} from "@/components/Leadership/leadershipData";
import Breadcrumbs from "@/components/Breadcrumbs";
const Banner = lazy(() => import("@/components/Leadership/Banner"));
const Advisors = lazy(() => import("@/components/Leadership/Advisors"));
const Card = lazy(() => import("@/components/Leadership/Card"));
const Founder = lazy(() => import("@/components/Leadership/Founder"));
const Buttons = lazy(() => import("@/components/Leadership/Buttons"));

const Leadership = () => {
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);
  const buttonsContainerRef = useRef(null);

  const scrollButtonIntoCenter = (index) => {
    const container = buttonsContainerRef.current;
    const button = container?.children[index];
    if (!button || !container) return;

    setTimeout(() => {
      const containerWidth = container.offsetWidth;
      const buttonOffsetLeft = button.offsetLeft;
      const buttonWidth = button.offsetWidth;
      container.scrollTo({
        left: buttonOffsetLeft - containerWidth / 2 + buttonWidth / 2,
        behavior: "smooth"
      });
    }, 10);
  };

  const scrollWithOffset = (index) => {
    let headerOffset = 130;

    if (window.innerWidth < 768) headerOffset = 80;
    if (window.innerWidth < 480) headerOffset = 100;

    const element = tabRefs.current[index] || document.getElementById(`section-${index}`);
    if (!element) return;

    setTimeout(() => {
      const rect = element.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const offsetPosition = rect.top + scrollY - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }, 100);
  };

  useEffect(() => {
    const getHeaderOffset = () => {
      let headerOffset = 130;
      if (window.innerWidth < 768) headerOffset = 80;
      if (window.innerWidth < 480) headerOffset = 100;
      return headerOffset;
    };

    let observer;

    const createObserver = () => {
      const headerOffset = getHeaderOffset();
      const rootMargin = `-${headerOffset}px 0px -50% 0px`;
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const index = tabRefs.current.findIndex((el) => el === entry.target);
              if (index !== -1) setActive(index);
            }
          });
        },
        { root: null, rootMargin, threshold: [0, 0.25, 0.5, 0.75, 1] }
      );

      tabRefs.current.forEach((el) => {
        if (el) observer.observe(el);
      });
    };

    const init = () => {
      // wait until refs are populated
      if (tabRefs.current && tabRefs.current.length > 0 && tabRefs.current.some(Boolean)) {
        createObserver();
      } else {
        // try again shortly
        const id = setTimeout(() => {
          init();
        }, 100);
        return () => clearTimeout(id);
      }
    }; 

    init();

    const handleResize = () => {
      if (observer) observer.disconnect();
      createObserver();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleActiveBtn = (index) => {
    setActive(index);
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <section className="w-full">
        <Banner />
        <Breadcrumbs items={[{ label: "Leadership" }]} />
      </section>

      <Buttons
        active={active}
        handleActiveBtn={handleActiveBtn}
        tabRefs={tabRefs}
        scrollWithOffset={scrollWithOffset}
        scrollButtonIntoCenter={scrollButtonIntoCenter}
        buttonsContainerRef={buttonsContainerRef}
      />

      <section
        id="section-0"
        className="w-full block md:flex justify-center bg-[#0B1622]"
        ref={(el) => (tabRefs.current[0] = el)}
      >
        <Founder />
      </section>

      <section
        id="section-1"
        className="w-full block md:flex justify-center"
        ref={(el) => (tabRefs.current[1] = el)}
      >
        <Advisors />
      </section>

      <section
        id="section-2"
        className="w-full block md:flex justify-center"
        ref={(el) => (tabRefs.current[2] = el)}
      >
        <div className="max-w-(--max-page-width) px-(--container-padding)  pt-6 md:pt-12">
          <div className="text-[18px] sm:text-[20px] md:text-[40px] font-bold leading-7 md:leading-9 ">
            Medhavi Group <span className="highlight-text">Leadership</span>
          </div>
          <div className="mt-3 flex items-center justify-start pb-6 md:pb-12">
            <div
              className="w-full max-w-50 md:max-w-[27%] h-1.75"
              style={{
                background: "linear-gradient(to right, #1291D4, #FFD200)",
              }}
            ></div>
          </div>

          <Card data={medhaviGroupLeadership} />
        </div>
      </section>

      <section className="w-full block md:flex justify-center">
        <div
          id="section-3"
          className="w-full max-w-(--max-page-width) px-(--container-padding) top_bottom_space"
          ref={(el) => (tabRefs.current[3] = el)}
        >
          <div className="text-[18px] sm:text-[20px] md:text-[40px] font-bold leading-7 md:leading-9">
            University <span className="highlight-text">Leadership</span>
          </div>
          <div className="mt-3 flex items-center justify-start pb-6 md:pb-12">
            <div
              className="w-full max-w-50 md:max-w-[20%] h-1.75"
              style={{
                background: "linear-gradient(to right, #1291D4, #FFD200)",
              }}
            ></div>
          </div>

          <Card data={universityLeadership} />
        </div>
      </section>

      <section className="w-full block md:flex justify-center">
        <div
          id="section-4"
          className="w-full max-w-(--max-page-width) px-(--container-padding) "
          ref={(el) => (tabRefs.current[4] = el)}
        >
          <div className="text-[18px] sm:text-[20px] md:text-[40px] font-bold leading-7 md:leading-9">
            Industry, WISE & Awarding Body{" "}
            <span className="highlight-text">Leadership</span>
          </div>
          <div className="mt-3 flex items-center justify-start pb-6 md:pb-12">
            <div
              className="w-full max-w-50 md:max-w-[40%] h-1.75"
              style={{
                background: "linear-gradient(to right, #1291D4, #FFD200)",
              }}
            ></div>
          </div>

          <Card data={industryWISEAwardingBody} />
        </div>
      </section>
    </Suspense>
  );
};

export default Leadership;
